import type { en } from './dictionaries/en'
import { getStoreLanguage } from '../providers/embedConfig'

/**
 * The dictionary shape is defined by the authoritative English dictionary. Other locales are
 * typed `Dict`, so every language must provide exactly the same keys (compile-time parity).
 */
export type Dict = typeof en

export type SupportedLocale = 'da' | 'en' | 'de' | 'sv' | 'fr' | 'nl' | 'nb'

export const SUPPORTED_LOCALES: SupportedLocale[] = ['da', 'en', 'de', 'sv', 'fr', 'nl', 'nb']
export const DEFAULT_LOCALE: SupportedLocale = 'en'

/**
 * Two-letter codes that mean a locale we ship under a different code. Core's `brandSetting.locales`
 * says `nb` for Norwegian, but a Shopify storefront reports the macrolanguage `no` (and `nn` for
 * Nynorsk) — without this, `window.Shopify.locale === 'no'` would silently fall back to English on a
 * Norwegian storefront.
 */
const LOCALE_ALIASES: Record<string, SupportedLocale> = { no: 'nb', nn: 'nb' }

/** Lowercase, strip any region suffix (`nb-NO` → `nb`) and resolve aliases. */
function normaliseLocale(value: string | null | undefined): string {
  const v = (value ?? '').toLowerCase().slice(0, 2)
  return LOCALE_ALIASES[v] ?? v
}

/** Map Core's `locale` (or anything) to a supported locale, falling back to the default. */
export function toSupportedLocale(value: string | null | undefined): SupportedLocale {
  const v = normaliseLocale(value)
  return (SUPPORTED_LOCALES as string[]).includes(v) ? (v as SupportedLocale) : DEFAULT_LOCALE
}

/**
 * The shop's own languages, narrowed to those we ship a dictionary for and de-duplicated (aliases can
 * collapse two entries into one). Drives the language switcher, so a Danish-only shop offers only
 * Danish rather than all seven bundled languages. Empty when `ShopLanguages` hasn't loaded yet or the
 * shop lists nothing we support — callers decide what to show in that case.
 */
export function toSupportedLocales(shopLanguages: readonly string[] | undefined): SupportedLocale[] {
  const seen = new Set<SupportedLocale>()
  for (const raw of shopLanguages ?? []) {
    const l = normaliseLocale(raw)
    if ((SUPPORTED_LOCALES as string[]).includes(l)) seen.add(l as SupportedLocale)
  }
  return [...seen]
}

/**
 * The default UI language before a session exists (the login page), resolved the same way as v1
 * (LocalizationProvider): detect the storefront's language from the embed config / `window.Shopify` /
 * the URL's Markets locale prefix (see embedConfig.getStoreLanguage), else the host page's `<html lang>`. If the shop offers that
 * language, use it; otherwise fall back to the shop's FIRST configured language — never the
 * browser's `navigator.language`, which is unrelated to the storefront. `shopLanguages` is Core's
 * `ShopLanguages(shopId)`, narrowed to those we ship a dictionary for.
 */
export function resolveDefaultLocale(shopLanguages: readonly string[] | undefined): SupportedLocale {
  const detected = normaliseLocale(
    getStoreLanguage() || (typeof document !== 'undefined' ? document.documentElement.lang : '') || '',
  )

  const supported = toSupportedLocales(shopLanguages)

  if (supported.includes(detected as SupportedLocale)) return detected as SupportedLocale
  if (supported.length > 0) return supported[0]
  // Shop languages not loaded yet (or none we support): use the detected language if we can, else
  // the default — so the page is never stuck on the wrong language while ShopLanguages loads.
  return (SUPPORTED_LOCALES as string[]).includes(detected) ? (detected as SupportedLocale) : DEFAULT_LOCALE
}

/** Values interpolated into a translation string via `{placeholder}`. */
export type TranslationValues = Record<string, string | number | undefined>

/**
 * A dictionary entry whose wording depends on the brand's voucher strategy (when the reward is
 * issued). `t()` auto-resolves the active strategy to one of these strings, so components keep calling
 * `t('confirm.heroBody')` unchanged. `received` is the default/fallback. Keys are the lowercased
 * VoucherStrategy values.
 */
export interface StrategyText {
  received: string
  handin: string
  instant: string
}
